import { FormDrawer, FormDrawerContent, Id, MultiSelectField, SelectedRows, isSuccess, useBulkEdit } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { applicationService, useApplicationRolesQuery } from "../shared/api/applicationService";
import { IRole } from "../shared/types";

interface Props {
	open: boolean;
	handleClose: () => void;
	userIds: SelectedRows;
	applicationId: string;
}

const initialRoles: IRole[] = [];

const getLabel = (role: IRole) => role.name;

const Wrapper = styled.div`
	display: grid;
	grid-auto-flow: row;
	gap: ${({ theme }) => theme.spacing(4)};
`;

export const ApplicationUsersRolesDrawer: FC<Props> = ({ open, handleClose, applicationId, userIds }) => {
	const { t } = useTranslation();
	const { data: roles } = useApplicationRolesQuery(applicationId);
	const queryClient = useQueryClient();
	const { setBulkEditing } = useBulkEdit();

	const updateApplicationUserRoles = useCallback(
		async (roleIds: Id[]) => {
			const response = await applicationService.updateRoles(applicationId, roleIds, userIds);

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes("applications") });
				setBulkEditing(false);
				return true;
			}

			return false;
		},
		[applicationId, queryClient, setBulkEditing, userIds],
	);

	return (
		<FormDrawer
			open={open}
			onSubmit={(values) => updateApplicationUserRoles(values.roles.map((role) => role.id))}
			initialValues={{ roles: initialRoles }}
			onClose={handleClose}
			title={t("Roles")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<Wrapper>
						<span>
							{t("{{ users }} user(s) is selected.", {
								users: userIds === "all" ? t("All") : `${userIds.length}`,
							})}
						</span>
						<MultiSelectField label={t("Roles")} name="roles" options={roles} getLabel={getLabel} />
					</Wrapper>
				</FormDrawerContent>
			)}
		/>
	);
};
